import React from 'react';
import { useTranslation } from 'react-i18next';

interface ProjectSettingsProps {
  units: 'metric' | 'imperial';
  tolerance: number;
  maintenanceFactor: number;
  bakeQuality: string;
  onUnitsChange: (units: string) => void;
  onToleranceChange: (tolerance: number) => void;
  onMaintenanceFactorChange: (factor: number) => void;
  onBakeQualityChange: (quality: string) => void;
}

export const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  units,
  tolerance,
  maintenanceFactor,
  bakeQuality,
  onUnitsChange,
  onToleranceChange,
  onMaintenanceFactorChange,
  onBakeQualityChange,
}) => {
  const { t } = useTranslation();

  const handleMaintenanceFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (value >= 0 && value <= 1) {
      onMaintenanceFactorChange(value);
    }
  };

  const handleToleranceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (value >= 0 && value <= 1) {
      onToleranceChange(value);
    }
  };

  const handleUnitsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUnitsChange(e.target.value as 'metric' | 'imperial');
  };

  const handleBakeQualityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onBakeQualityChange(e.target.value);
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-[486px] border-b border-greyP5 p-4 md:p-8">
      <div className="w-full lg:w-[30%] lg:pr-8 mb-6 lg:mb-0">
        <div className="flex flex-col items-center">
          <div className="p-0 lg:p-5 text-white text-sm md:text-md w-full">
            <p>{t('settings.project.description')}</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[70%] lg:pl-8">
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-5 border-b border-greyP4 gap-4">
            <div className="flex flex-col">
              <span className="text-white text-sm md:text-md font-medium">
                {t('settings.project.maintenanceFactor')}
              </span>
              <span className="text-gray-500 text-xs mt-1">{t('settings.project.rangeHint')}</span>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={maintenanceFactor}
                onChange={handleMaintenanceFactorChange}
                className="w-20 md:w-24 px-3 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-green01 transition-colors duration-200"
              />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={maintenanceFactor}
                onChange={handleMaintenanceFactorChange}
                className="w-24 md:w-32 accent-green01"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-5 border-b border-greyP4 gap-4">
            <div className="flex flex-col">
              <span className="text-white text-sm md:text-md font-medium">{t('settings.project.tolerance')}</span>
              <span className="text-gray-500 text-xs mt-1">{t('settings.project.rangeHint')}</span>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={tolerance}
                onChange={handleToleranceChange}
                className="w-20 md:w-24 px-3 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-green01 transition-colors duration-200"
              />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={tolerance}
                onChange={handleToleranceChange}
                className="w-24 md:w-32 accent-green01"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-5 border-b border-greyP4 gap-4">
            <div className="flex flex-col">
              <span className="text-white text-sm md:text-md font-medium">{t('settings.project.units')}</span>
              <span className="text-gray-500 text-xs mt-1">{t('settings.project.measurementSystem')}</span>
            </div>
            <select
              value={units.toLowerCase()}
              onChange={handleUnitsChange}
              className="w-full sm:w-auto px-4 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-green01 transition-colors duration-200 cursor-pointer"
            >
              <option value="metric">{t('settings.project.metric')}</option>
              <option value="imperial">{t('settings.project.imperial')}</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-5 gap-4">
            <div className="flex flex-col">
              <span className="text-white text-sm md:text-md font-medium">{t('settings.project.bakeQuality')}</span>
              <span className="text-gray-500 text-xs mt-1">{t('settings.project.bakeQualityHint')}</span>
            </div>
            <select
              value={bakeQuality}
              onChange={handleBakeQualityChange}
              className="w-full sm:w-auto px-4 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-green01 transition-colors duration-200 cursor-pointer"
            >
              <option value="PRODUCTION">{t('settings.project.production')}</option>
              <option value="PREVIEW">{t('settings.project.preview')}</option>
              <option value="DRAFT">{t('settings.project.draft')}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
